import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { useCurrentApplication, useFormatMessage } from 'app/common/hooks';
import itemAvailabilityMessages from '../ItemAvailability.messages';
import {
  dealerInStockLabel,
  pdcInStockLabel,
  useInventoryAvailabilityLabel,
  useInventoryMessageContent
} from 'app/common/hooks/inventory/useInventoryMessaging/useInventoryMessaging';
import cx from 'classnames';

const InventoryIndicator = ({
  requestedQuantity,
  locationQtys,
  textColor = 'text-gray-700'
}) => {
  const formatMessage = useFormatMessage();
  const { identifierValue, preferredPdc } = useCurrentApplication();
  const inventoryMessagesContent = useInventoryMessageContent();

  const dealerLabel = useInventoryAvailabilityLabel(
    inventoryMessagesContent,
    dealerInStockLabel,
    true
  );

  const pdcLabel = useInventoryAvailabilityLabel(
    inventoryMessagesContent,
    pdcInStockLabel
  );

  // calculate how much requested quantity is available from dealer, pdc, or backorder
  const { dealerQty, pdcQty, backorderQty } = useMemo(() => {
    if (isEmpty(locationQtys)) {
      return {
        dealerQty: 0,
        pdcQty: 0,
        backorderQty: 0
      };
    }

    const dealerQtyAvail = get(locationQtys, identifierValue, 0);
    const pdcQtyAvail = get(locationQtys, preferredPdc, 0);

    let remaining = requestedQuantity;

    const dQty = Math.min(remaining, dealerQtyAvail);
    remaining -= dQty;

    const pQty = Math.min(remaining, pdcQtyAvail);
    remaining -= pQty;

    return {
      dealerQty: dQty,
      pdcQty: pQty,
      backorderQty: remaining
    };
  }, [requestedQuantity, locationQtys, identifierValue, preferredPdc]);

  return (
    <div className={cx(textColor, 'space-y-1 text-sm')}>
      {dealerQty > 0 && dealerLabel && (
        <p>
          {formatMessage(itemAvailabilityMessages.inventoryLocationMessage, {
            quantity: dealerQty,
            message: dealerLabel
          })}
        </p>
      )}
      {pdcQty > 0 && pdcLabel && (
        <p>
          {formatMessage(itemAvailabilityMessages.inventoryLocationMessage, {
            quantity: pdcQty,
            message: pdcLabel
          })}
        </p>
      )}
      {backorderQty > 0 && (
        <p>
          {formatMessage(itemAvailabilityMessages.backorder, {
            quantity: backorderQty
          })}
        </p>
      )}
    </div>
  );
};

InventoryIndicator.propTypes = {
  locationQtys: PropTypes.object,
  requestedQuantity: PropTypes.number,
  sku: PropTypes.string,
  textColor: PropTypes.string
};

export default InventoryIndicator;
